import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useAuthStore } from '../stores/authStore';
import { Role } from '../auth/roles';
import { formatDate, formatEUR } from '../lib/format';
import { cn } from '../utils/cn';
import { PageHeader } from '../components/ui/PageHeader';

interface RefundRow {
  id: string;
  source: 'RETURN' | 'ACCOUNT_CREDIT';
  channel: 'CAWL' | 'CARD_TERMINAL' | 'CASH' | 'BANK_TRANSFER';
  amount: number;
  status: 'PENDING_APPROVAL' | 'PENDING' | 'DONE' | 'FAILED' | 'CANCELLED';
  reason: string;
  reference: string | null;
  failure: string | null;
  iban: string | null;
  requestedAt: string;
  executedAt: string | null;
  returnNumber: string | null;
  orderNumber: string | null;
  creditNoteNumber: string | null;
  customerName: string | null;
  requestedBy: string | null;
  approvedBy: string | null;
  executedBy: string | null;
}

const TONE: Record<RefundRow['status'], string> = {
  FAILED: 'bg-destructive/10 text-destructive',
  PENDING_APPROVAL: 'bg-amber-100 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200',
  PENDING: 'bg-primary/10 text-primary',
  DONE: 'bg-green-100 text-green-900 dark:bg-green-950/40 dark:text-green-200',
  CANCELLED: 'bg-muted text-muted-foreground',
};

/**
 * Refunds (migration 135). FAILED first — the reconciliation list — then what waits for a
 * manager's approval, then what the till must do by hand: the counter terminal, cash, a bank
 * transfer to the IBAN shown. Online card refunds (CAWL) run on their own. Every action keeps
 * its operator and reference.
 */
export default function Refunds() {
  const { t } = useTranslation();
  const { push, showApiError } = useToast();
  const role = useAuthStore((s) => s.user?.role);
  const isManager = role === Role.MANAGER || role === Role.OWNER || role === Role.ADMIN;
  const [rows, setRows] = useState<RefundRow[] | null>(null);
  const [showDone, setShowDone] = useState(false);
  const [refs, setRefs] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    apiClient
      .get<{ data: RefundRow[] }>('/refunds', {
        params: showDone ? {} : { status: 'FAILED,PENDING_APPROVAL,PENDING' },
      })
      .then((r) => setRows(r.data.data))
      .catch(showApiError);
  }, [showDone, showApiError]);
  useEffect(load, [load]);

  const act = async (id: string, path: string, body: unknown = {}) => {
    setBusy(id);
    try {
      await apiClient.post(`/refunds/${id}/${path}`, body, {
        headers: { 'X-Command-Id': crypto.randomUUID() },
      });
      push({ tone: 'success', message: t(`refunds.done.${path}`) });
      load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('refunds.title')}
        description={t('refunds.intro')}
        actions={
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={showDone}
              onChange={(e) => setShowDone(e.target.checked)}
            />
            {t('refunds.showDone')}
          </label>
        }
      />

      {rows && rows.length === 0 && (
        <p className="rounded border bg-card p-4 text-sm text-muted-foreground">
          {t('refunds.empty')}
        </p>
      )}

      <ul className="space-y-2">
        {rows?.map((r) => (
          <li key={r.id} className="rounded border bg-card p-3 text-sm">
            <div className="flex flex-wrap items-center gap-3">
              <span className={cn('rounded px-2 py-0.5 text-xs font-semibold', TONE[r.status])}>
                {t(`refunds.status.${r.status}`)}
              </span>
              <span className="font-semibold tabular-nums">{formatEUR(r.amount)}</span>
              <span>{t(`refunds.channel.${r.channel}`)}</span>
              {r.source === 'ACCOUNT_CREDIT' && (
                <span className="rounded bg-muted px-2 py-0.5 text-xs">
                  {t('refunds.accountCredit')}
                </span>
              )}
              <span className="text-muted-foreground">
                {r.customerName ?? '—'} · {r.orderNumber ?? ''}
                {r.returnNumber ? ` · ${r.returnNumber}` : ''}
                {r.creditNoteNumber ? ` · ${r.creditNoteNumber}` : ''}
              </span>
              <span className="ml-auto text-xs text-muted-foreground">
                {formatDate(r.requestedAt)}
              </span>
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {t('refunds.reason')}: {r.reason} ·{' '}
              {t('refunds.requestedBy', { name: r.requestedBy ?? '—' })}
              {r.approvedBy ? ` · ${t('refunds.approvedBy', { name: r.approvedBy })}` : ''}
              {r.executedBy ? ` · ${t('refunds.executedBy', { name: r.executedBy })}` : ''}
              {r.reference ? ` · ${t('refunds.ref', { ref: r.reference })}` : ''}
            </div>
            {r.channel === 'BANK_TRANSFER' && r.iban && r.iban !== 'MISSING' && (
              <div className="mt-1 text-xs">
                IBAN : <span className="font-mono">{r.iban}</span>
              </div>
            )}
            {r.failure && <div className="mt-1 text-xs text-destructive">{r.failure}</div>}

            <div className="mt-2 flex flex-wrap items-center gap-2">
              {r.status === 'PENDING_APPROVAL' && isManager && (
                <Button
                  size="sm"
                  disabled={busy === r.id}
                  onClick={() => void act(r.id, 'approve')}
                >
                  {t('refunds.approve')}
                </Button>
              )}
              {r.status === 'PENDING_APPROVAL' && !isManager && (
                <span className="text-xs text-muted-foreground">{t('refunds.needsManager')}</span>
              )}
              {r.status === 'PENDING' && r.channel !== 'CAWL' && (
                <>
                  <Input
                    className="h-8 w-56"
                    placeholder={t(`refunds.refPlaceholder.${r.channel}`)}
                    value={refs[r.id] ?? ''}
                    onChange={(e) => setRefs((x) => ({ ...x, [r.id]: e.target.value }))}
                  />
                  <Button
                    size="sm"
                    disabled={busy === r.id || (refs[r.id] ?? '').trim().length < 3}
                    onClick={() => void act(r.id, 'confirm', { reference: refs[r.id] })}
                  >
                    {t(`refunds.confirm.${r.channel}`)}
                  </Button>
                </>
              )}
              {r.status === 'FAILED' && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={busy === r.id}
                  onClick={() => void act(r.id, 'retry')}
                >
                  {t('refunds.retry')}
                </Button>
              )}
              {['PENDING_APPROVAL', 'PENDING', 'FAILED'].includes(r.status) && isManager && (
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={busy === r.id}
                  onClick={() => {
                    const reason = window.prompt(t('refunds.cancelPrompt'));
                    if (reason) void act(r.id, 'cancel', { reason });
                  }}
                >
                  {t('refunds.cancel')}
                </Button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
